/**
 * CoachPlaybook - Theme Context Provider
 * Version: 0.6.0
 * Description: React context for light/dark mode with localStorage persistence
 */

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

const THEMES = {
  dark: {
    bg: '#0f1420', bgPanel: '#0a0e17', bgCard: '#1e293b', bgInput: '#1e293b',
    border: '#334155', borderLight: '#475569',
    text: '#f1f5f9', textSecondary: '#94a3b8', textMuted: '#64748b',
    accent: '#3b82f6', accentHover: '#2563eb',
    danger: '#ef4444', success: '#22c55e', warning: '#f59e0b',
    glass: 'rgba(15,23,42,0.85)', glassBorder: 'rgba(51,65,85,0.5)', shadow: 'rgba(0,0,0,0.3)',
  },
  light: {
    bg: '#f8fafc', bgPanel: '#ffffff', bgCard: '#ffffff', bgInput: '#f1f5f9',
    border: '#e2e8f0', borderLight: '#cbd5e1',
    text: '#1e293b', textSecondary: '#64748b', textMuted: '#94a3b8',
    accent: '#2563eb', accentHover: '#1d4ed8',
    danger: '#dc2626', success: '#16a34a', warning: '#d97706',
    glass: 'rgba(255,255,255,0.85)', glassBorder: 'rgba(226,232,240,0.5)', shadow: 'rgba(0,0,0,0.08)',
  },
};

const getTheme = (mode) => THEMES[mode] || THEMES.dark;

const ThemeContext = createContext(null);

const STORAGE_KEY = 'cp_theme';

const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'light' || saved === 'dark') return saved;
    } catch (e) { /* localStorage unavailable */ }
    return 'dark';
  });

  // Persist to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch (e) { /* localStorage unavailable */ }
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
  };

  const theme = useMemo(() => getTheme(mode), [mode]);

  const value = useMemo(() => ({
    mode,
    theme,
    toggleTheme,
    setMode,
  }), [mode, theme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    // Fallback so unconverted components don't crash
    return { mode: 'dark', theme: getTheme('dark'), toggleTheme: () => {}, setMode: () => {} };
  }
  return ctx;
};

export { ThemeProvider, useTheme, ThemeContext };
export default ThemeProvider;
